import React, { useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Container,
  CTA,
  DisclaimerLayer,
  Text,
  TokenTextAppearance,
  styled,
  TextTag,
} from '@volkswagen-onehub/components-core';
import { Close, Info } from '@volkswagen-onehub/icons-core';
import { GiveawayContext } from '@/state/state';
import { minimalScreenWidth } from '@/common/breakpoints';
import { formatPrice, isOrderPrice } from '@/util/currency';

const StyledPriceCard = styled(Card)`
  box-sizing: border-box;
  grid-area: price;
  align-self: start;
  width: 100%;
  overflow: visible;
  padding: ${(props) => props.theme.size.static300};
  gap: ${(props) => props.theme.size.static250};
  border: var(--card-border, 0);
  border-radius: var(--card-border-radius, ${(props) => props.theme.size.borderRadius.card});
  box-shadow: var(--card-hover-shadow, #997f67 0 0 0 1px inset, 0 4px 10px 0 rgba(49, 25, 2, 0.16));

  &:hover:not(:disabled) {
    box-shadow: var(
      --card-hover-shadow,
      #997f67 0 0 0 1px inset,
      0 4px 10px 0 rgba(49, 25, 2, 0.16)
    );
  }

  @media screen and ${minimalScreenWidth.l} {
    width: 280px;
    justify-self: end;
  }
`;

const StyledPriceBlock = styled.div`
  display: grid;
  gap: 0;
`;

const StyledPriceRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${(props) => props.theme.size.static100};
`;

const StyledInfoButton = styled.div`
  flex-shrink: 0;

  button {
    min-width: ${(props) => props.theme.size.static300};
    min-height: ${(props) => props.theme.size.static300};
    width: ${(props) => props.theme.size.static300};
    height: ${(props) => props.theme.size.static300};
    padding: 0;
  }

  svg {
    width: ${(props) => props.theme.size.static300};
    height: ${(props) => props.theme.size.static300};
  }
`;

const StyledDisclaimerContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${(props) => props.theme.size.static200};

  > :first-child {
    flex: 1;
    min-width: 0;
  }
`;

const StyledDisclaimerPortal = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  pointer-events: none;

  [role='dialog'] {
    pointer-events: auto;
  }
`;

export const PriceCard = (): React.ReactElement | null => {
  const { t } = useTranslation();
  const { pricing, locale } = useContext(GiveawayContext);
  const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false);

  if (!isOrderPrice(pricing)) {
    return null;
  }

  const freeText = pricing.freeTextField?.trim();

  return (
    <StyledPriceCard data-testid="yourcar-pricing">
      <StyledPriceBlock>
        <StyledPriceRow>
          <Text tag={TextTag.h2} bold appearance={TokenTextAppearance.headline400}>
            {formatPrice(pricing.amount, pricing.currency, locale)}
          </Text>
          <StyledInfoButton>
            <CTA
              tag="button"
              type="button"
              size="small"
              emphasis="tertiary"
              icon={<Info variant="default" aria-hidden />}
              aria-label={t('header.pricingDisclaimerAriaLabel')}
              data-testid="yourcar-pricing-info"
              onClick={() => setIsDisclaimerOpen(true)}
            />
          </StyledInfoButton>
        </StyledPriceRow>
        {pricing.tax?.description && (
          <Text appearance={TokenTextAppearance.headline300} data-testid="yourcar-pricing-tax">
            {pricing.tax.description}
          </Text>
        )}
      </StyledPriceBlock>
      {freeText && (
        <Text
          tag={TextTag.p}
          appearance={TokenTextAppearance.label100}
          data-testid="yourcar-pricing-freetext"
        >
          {freeText}
        </Text>
      )}
      {isDisclaimerOpen &&
        createPortal(
          <StyledDisclaimerPortal>
            <DisclaimerLayer
              in
              appearance="dark"
              aria-label={t('header.pricingDisclaimerAriaLabel')}
              data-testid="yourcar-pricing-disclaimer"
            >
              <Container padding="static350">
                <StyledDisclaimerContent>
                  <Text appearance={TokenTextAppearance.copy100}>
                    {t('header.pricingDisclaimer')}
                  </Text>
                  <CTA
                    tag="button"
                    type="button"
                    size="small"
                    emphasis="tertiary"
                    icon={<Close aria-hidden />}
                    aria-label={t('equipmentDialog.closeButtonAriaLabel')}
                    data-testid="yourcar-pricing-disclaimer-close"
                    onClick={() => setIsDisclaimerOpen(false)}
                  />
                </StyledDisclaimerContent>
              </Container>
            </DisclaimerLayer>
          </StyledDisclaimerPortal>,
          document.body,
        )}
    </StyledPriceCard>
  );
};
